import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import PrivateRoute from './PrivateRoute';
import PublicRoute from './PublicRoute';
import useFlagEnabled from 'hooks/useFlagEnabled';
import useShowQuickActionsDashboard from 'hooks/useShowQuickActionsDashboard';

// Lazy loading the larger components to improve performance

import LoginSuccessPage from 'containers/LoginSuccessPage';
import LogoutPage from 'containers/LogoutPage';
import OnboardingPage from 'containers/OnboardingPage';
import SignInPage from 'containers/SignInPage';
import SignInSuccessPage from 'containers/SignInSuccessPage';
import SignUpPage from 'containers/SignUpPage';

import Preferences from 'containers/SettingsPage/PreferencesTab';
import CompanySettings from 'containers/SettingsPage/CompanyTab';
import Dropshipping from 'containers/SettingsPage/DropshippingTab';
import MarketplaceSettings from 'containers/SettingsPage/MarketplaceTab';
import BrandSettings from 'containers/SettingsPage/MarketplaceTab/BrandSettings';
import SocialLinks from 'containers/SettingsPage/MarketplaceTab/SocialLinks';
import Attributes from 'containers/SettingsPage/MarketplaceTab/Attributes';
import Assets from 'containers/SettingsPage/MarketplaceTab/Assets';
import Shipping from 'containers/SettingsPage/MarketplaceTab/Shipping';
import ServiceOptions from 'containers/SettingsPage/MarketplaceTab/ServiceOptions';
import AccountSettings from 'containers/SettingsPage/PreferencesTab/AccountSettings';
import AdminSettings from 'containers/SettingsPage/PreferencesTab/AdminSettings';
import BillingSettings from 'containers/SettingsPage/DropshippingTab/BillingSettings';
import ShippingSettings from 'containers/SettingsPage/DropshippingTab/ShippingSettings';
import IntegrationsSettings from 'containers/SettingsPage/DropshippingTab/IntegrationsSettings';
import WebhooksSettings from 'containers/SettingsPage/DropshippingTab/WebhooksSettings';
import NotificationsSettings from 'containers/SettingsPage/DropshippingTab/NotificationsSettings';
import MerchandisingSettings from 'containers/SettingsPage/DropshippingTab/MerchandisingSettings';
import ReturnsSettings from 'containers/SettingsPage/DropshippingTab/ReturnsSettings';
import CompanyProfileSettings from 'containers/SettingsPage/CompanyTab/CompanyProfileSettings';
import CompanyLocationSettings from 'containers/SettingsPage/CompanyTab/CompanyLocationsSettings';
import TeamMembersSettings from 'containers/SettingsPage/CompanyTab/TeamMembersSettings';
import CompanyFlagsSettings from 'containers/SettingsPage/CompanyTab/CompanyFlagsSettings';

const MarketplaceHomePage = lazy(() => import('containers/Marketplace/HomePage'));
const MarketplaceBrandPage = lazy(() => import('containers/Marketplace/BrandPage'));
const MarketplaceProductPage = lazy(() => import('containers/Marketplace/ProductPage'));
const MarketplaceCategoryPage = lazy(() => import('containers/Marketplace/CategoryPage'));
const MarketplaceSearchPage = lazy(() => import('containers/Marketplace/SearchPage'));
const MarketplaceDiscoverPage = lazy(() => import('containers/Marketplace/DiscoverPage'));
const AgreementPage = lazy(() => import('containers/Agreements/AgreementPage'));
const DownloadPage = lazy(() => import('containers/Download/DownloadPage'));
const EDISchemaPage = lazy(() => import('containers/EDISchemas/EDISchemaPage'));
const EDISchemasPage = lazy(() => import('containers/EDISchemas/EDISchemasPage'));
const GetStartedPage = lazy(() => import('containers/GetStarted/GetStartedPage'));
const HomePage = lazy(() => import('containers/HomePage'));
const InvoicePage = lazy(() => import('containers/Invoices/InvoicePage'));
const InvoicesPage = lazy(() => import('containers/Invoices/InvoicesPage'));
const LockedPage = lazy(() => import('containers/LockedPage'));
const OrderPage = lazy(() => import('containers/OrderPage'));
const OrderPackingSlipPage = lazy(() => import('containers/OrderPage/OrderPackingSlipPage'));
const OTAOrderPackingSlipPage = lazy(() => import('containers/OrderPage/OTAOrderPackingSlipPage'));
const OrdersPage = lazy(() => import('containers/OrdersPage'));
const QuickActionsPage = lazy(() => import('containers/QuickActions/QuickActionsPage'));
const PartnerPage = lazy(() => import('containers/PartnerPage'));
const PartnersPage = lazy(() => import('containers/PartnersPage'));
const RequestsPage = lazy(() => import('containers/RequestsPage'));
const PriceListPage = lazy(() => import('containers/PriceListPage'));
const PricesPage = lazy(() => import('containers/PriceListsPage'));
const CreatePriceList = lazy(() => import('containers/PriceListsPage/CreatePriceList'));
const ProductPage = lazy(() => import('containers/Product/ProductPage'));
const ProductsPage = lazy(() => import('containers/Products'));
const SettingsPage = lazy(() => import('containers/SettingsPage'));
const TradeOpsIssuesPage = lazy(() => import('containers/TradeOpsIssues/TradeOpsIssuesPage'));
const NotFoundPage = lazy(() => import('./NotFoundPage'));

const AppRouter = () => {
  const showMarketplace = useFlagEnabled('showMarketplace');
  const showShippingPage = useFlagEnabled('showShippingPage');
  const showQuickActionsDashboard = useShowQuickActionsDashboard();
  return (
    <Routes>
      {/* Home */}
      <Route
        path="/"
        element={
          <PrivateRoute>
            <HomePage />
          </PrivateRoute>
        }
      />

      {/* Auth */}
      <Route path="/login" element={<Navigate to="/signin" replace />} />
      <Route
        path="/signup"
        element={
          <PublicRoute>
            <SignUpPage />
          </PublicRoute>
        }
      />
      <Route
        path="/signin"
        element={
          <PublicRoute>
            <SignInPage />
          </PublicRoute>
        }
      />
      <Route
        path="/signin/success"
        element={
          <PublicRoute>
            <SignInSuccessPage />
          </PublicRoute>
        }
      />
      <Route
        path="/login/:token"
        element={
          <PublicRoute>
            <LoginSuccessPage />
          </PublicRoute>
        }
      />
      <Route
        path="/logout"
        element={
          <PublicRoute>
            <LogoutPage />
          </PublicRoute>
        }
      />

      <Route
        path="/locked"
        element={
          <PrivateRoute>
            <LockedPage />
          </PrivateRoute>
        }
      />

      {/* Onboarding */}
      <Route
        path="/onboarding"
        element={
          <PrivateRoute showNav={false}>
            <OnboardingPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/get-started"
        element={
          <PrivateRoute>
            <GetStartedPage />
          </PrivateRoute>
        }
      />

      {/* Marketplace */}
      <Route
        path="/marketplace/discover"
        element={
          <PrivateRoute>
            <MarketplaceDiscoverPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/marketplace/home/:type"
        element={
          <PrivateRoute>
            {showMarketplace ? <MarketplaceHomePage /> : <Navigate to="/" replace />}
          </PrivateRoute>
        }
      />
      <Route
        path="/marketplace/brand/:brandId"
        element={
          <PrivateRoute>
            {showMarketplace ? <MarketplaceBrandPage /> : <Navigate to="/" replace />}
          </PrivateRoute>
        }
      />
      <Route
        path="/marketplace/category/:categoryId"
        element={
          <PrivateRoute>
            {showMarketplace ? <MarketplaceCategoryPage /> : <Navigate to="/" replace />}
          </PrivateRoute>
        }
      />
      <Route
        path="/marketplace/search/:keyword"
        element={
          <PrivateRoute>
            {showMarketplace ? <MarketplaceSearchPage /> : <Navigate to="/" replace />}
          </PrivateRoute>
        }
      />
      <Route
        path="/marketplace/product/:productId"
        element={
          <PrivateRoute>
            {showMarketplace ? <MarketplaceProductPage /> : <Navigate to="/" replace />}
          </PrivateRoute>
        }
      />
      {/* Overview */}
      <Route
        path="/overview"
        element={
          <PrivateRoute>
            {showQuickActionsDashboard ? <QuickActionsPage /> : <Navigate to="/" replace />}
          </PrivateRoute>
        }
      />
      {/* Orders */}
      <Route
        path="/orders"
        element={
          <PrivateRoute>
            <OrdersPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/orders/:orderId"
        element={
          <PrivateRoute>
            <OrderPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/orders/:orderId/packingslip"
        element={
          <PrivateRoute showNav={false}>
            <OrderPackingSlipPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/ota/orders/:orderId/packingslip"
        element={
          <PublicRoute>
            <OTAOrderPackingSlipPage />
          </PublicRoute>
        }
      />

      {/* Partners */}
      <Route
        path="/partners"
        element={
          <PrivateRoute>
            <PartnersPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/partners/:partnerId"
        element={
          <PrivateRoute>
            <PartnerPage />
          </PrivateRoute>
        }
      />

      {/* Requests */}
      <Route
        path="/requests/:type"
        element={
          <PrivateRoute>
            <RequestsPage />
          </PrivateRoute>
        }
      />

      {/* Products */}
      <Route
        path="/products"
        element={
          <PrivateRoute>
            <ProductsPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/products/:productId"
        element={
          <PrivateRoute>
            <ProductPage />
          </PrivateRoute>
        }
      />

      {/* Settings */}
      <Route
        path="settings"
        element={
          <PrivateRoute>
            <SettingsPage />
          </PrivateRoute>
        }
      >
        <Route
          path="preferences"
          element={
            <Suspense fallback={null}>
              <Preferences />
            </Suspense>
          }
        >
          <Route path="account" element={<AccountSettings />} />
          <Route path="admin" element={<AdminSettings />} />
          <Route index element={<Navigate to="account" replace />} />
        </Route>
        <Route
          path="company"
          element={
            <Suspense fallback={null}>
              <CompanySettings />
            </Suspense>
          }
        >
          <Route path="profile" element={<CompanyProfileSettings />} />
          <Route path="locations" element={<CompanyLocationSettings />} />
          <Route path="team-members" element={<TeamMembersSettings />} />
          <Route path="rules" element={<CompanyFlagsSettings />} />
          <Route index element={<Navigate to="profile" replace />} />
        </Route>
        <Route
          path="dropshipping"
          element={
            <Suspense fallback={null}>
              <Dropshipping />
            </Suspense>
          }
        >
          <Route path="billing" element={<BillingSettings />} />
          <Route
            path="shipping"
            element={
              showShippingPage ? (
                <ShippingSettings />
              ) : (
                <Navigate to="/settings/dropshipping/billing" replace />
              )
            }
          />
          <Route path="integrations" element={<IntegrationsSettings />} />
          <Route path="webhooks" element={<WebhooksSettings />} />
          <Route path="notifications" element={<NotificationsSettings />} />
          <Route path="merchandising" element={<MerchandisingSettings />} />
          <Route
            path="service-levels"
            element={<Navigate to="/settings/dropshipping/billing" replace />}
          />
          <Route path="returns" element={<ReturnsSettings />} />
          <Route index element={<Navigate to="billing" replace />} />
        </Route>
        <Route
          path="marketplace"
          element={
            <Suspense fallback={null}>
              <MarketplaceSettings />
            </Suspense>
          }
        >
          <Route path="brand" element={<BrandSettings />} />
          <Route path="social" element={<SocialLinks />} />
          <Route path="attributes" element={<Attributes />} />
          <Route path="assets" element={<Assets />} />
          <Route path="shipping" element={<Shipping />} />
          <Route path="service-options" element={<ServiceOptions />} />
          <Route index element={<Navigate to="brand" replace />} />
        </Route>
        <Route index element={<Navigate to="preferences/account" replace />} />
      </Route>

      {/* TradeOps */}
      <Route
        path="/issues"
        element={
          <PrivateRoute>
            <TradeOpsIssuesPage />
          </PrivateRoute>
        }
      />

      {/* Pricing */}
      <Route
        path="/prices"
        element={
          <PrivateRoute>
            <PricesPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/prices/create"
        element={
          <PrivateRoute>
            <CreatePriceList />
          </PrivateRoute>
        }
      />
      <Route
        path="/prices/:priceId"
        element={
          <PrivateRoute>
            <PriceListPage />
          </PrivateRoute>
        }
      />

      {/* Invoices */}
      <Route
        path="/invoices"
        element={
          <PrivateRoute>
            <InvoicesPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/invoices/:id"
        element={
          <PrivateRoute>
            <InvoicePage />
          </PrivateRoute>
        }
      />

      {/* Agreements */}
      <Route
        path="/agreements/:agreementId"
        element={
          <PrivateRoute>
            <AgreementPage />
          </PrivateRoute>
        }
      />

      {/* EDI */}
      <Route
        path="/edi/documents"
        element={
          <PrivateRoute showNav={false}>
            <EDISchemasPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/edi/documents/:documentNumber"
        element={
          <PrivateRoute showNav={false}>
            <EDISchemaPage />
          </PrivateRoute>
        }
      />

      {/* Download */}
      <Route
        path="/download/:documentId"
        element={
          <PrivateRoute showNav={false}>
            <DownloadPage />
          </PrivateRoute>
        }
      />

      {/* Reporting */}
      <Route path="/reporting" element={<Navigate to="/" replace />} />

      {/* 404 */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
};

export default AppRouter;
